/**
 * RequestThreadPage - Public page for delegated task completion.
 *
 * Uses TaskInteractionPanel for a unified form + chat experience.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_REQUEST_THREAD_PUBLIC,
  POST_THREAD_MESSAGE,
  UPDATE_THREAD_FIELDS,
  CONFIRM_THREAD_SATISFACTION,
} from '../graphql/queries';
import { RequestThreadStatus } from '../types/generated/graphql';
import { TaskInteractionPanel, type ChatMessage, type SatisfactionState } from '../components/shared';
import logo from '../images/logo.png';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

type PageState = 'loading' | 'ready' | 'success' | 'error' | 'closed';

export default function RequestThreadPage() {
  const { slug } = useParams<{ slug: string }>();

  const [state, setState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [satisfaction, setSatisfaction] = useState<SatisfactionState | null>(null);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [updatingField, setUpdatingField] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Fetch thread public data
  const { data, loading } = useQuery(GET_REQUEST_THREAD_PUBLIC, {
    variables: { slug: slug || '' },
    skip: !slug,
    onCompleted: (data) => {
      if (!data.requestThreadPublic) {
        setState('error');
        setErrorMessage('Request thread not found');
        return;
      }

      const thread = data.requestThreadPublic;

      if (thread.status === RequestThreadStatus.Satisfied) {
        setState('success');
        return;
      }

      if (
        thread.status === RequestThreadStatus.Expired ||
        thread.status === RequestThreadStatus.Cancelled
      ) {
        setState('closed');
        setErrorMessage(
          thread.status === RequestThreadStatus.Expired
            ? 'This request has expired.'
            : 'This request has been cancelled.'
        );
        return;
      }

      // Map messages to ChatMessage type
      const mappedMessages: ChatMessage[] = (thread.messages || []).map((msg: { id: string; role: string; content: string; timestamp: string }) => ({
        id: msg.id,
        role: msg.role as 'USER' | 'ASSISTANT' | 'SYSTEM',
        content: msg.content,
        timestamp: msg.timestamp,
      }));
      setMessages(mappedMessages);

      // Map satisfaction state
      if (thread.satisfactionState) {
        setSatisfaction({
          isReady: thread.satisfactionState.isReady,
          confidence: thread.satisfactionState.confidence,
          extractedFields: thread.satisfactionState.extractedFields as Record<string, unknown>,
          missingFields: thread.satisfactionState.missingFields || [],
          progressSummary: thread.satisfactionState.progressSummary || '',
        });
        setFormValues(thread.satisfactionState.extractedFields as Record<string, unknown> || {});
      }

      setState('ready');
    },
    onError: (error) => {
      setState('error');
      setErrorMessage(error.message || 'Failed to load request thread');
    },
  });

  // Post message mutation
  const [postMessage, { loading: posting }] = useMutation(POST_THREAD_MESSAGE, {
    onCompleted: (data) => {
      const result = data.postThreadMessage;
      const mappedMessages: ChatMessage[] = (result.requestThread.messages || []).map((msg: { id: string; role: string; content: string; timestamp: string }) => ({
        id: msg.id,
        role: msg.role as 'USER' | 'ASSISTANT' | 'SYSTEM',
        content: msg.content,
        timestamp: msg.timestamp,
      }));
      setMessages(mappedMessages);

      if (result.satisfactionState) {
        setSatisfaction({
          isReady: result.satisfactionState.isReady,
          confidence: result.satisfactionState.confidence,
          extractedFields: result.satisfactionState.extractedFields as Record<string, unknown>,
          missingFields: result.satisfactionState.missingFields || [],
          progressSummary: result.satisfactionState.progressSummary || '',
        });
        setFormValues(result.satisfactionState.extractedFields as Record<string, unknown> || {});
      }
    },
    onError: (error) => {
      setErrorMessage(error.message || 'Failed to send message');
    },
  });

  // Update thread fields mutation
  const [updateFields] = useMutation(UPDATE_THREAD_FIELDS, {
    onCompleted: (data) => {
      const result = data.updateThreadFields;
      setUpdatingField(false);

      if (result.satisfactionState) {
        setSatisfaction({
          isReady: result.satisfactionState.isReady,
          confidence: result.satisfactionState.confidence,
          extractedFields: result.satisfactionState.extractedFields as Record<string, unknown>,
          missingFields: result.satisfactionState.missingFields || [],
          progressSummary: result.satisfactionState.progressSummary || '',
        });
        setFormValues(result.satisfactionState.extractedFields as Record<string, unknown> || {});
      }
    },
    onError: (error) => {
      setErrorMessage(error.message || 'Failed to update field');
      setUpdatingField(false);
    },
  });

  // Confirm satisfaction mutation
  const [confirmSatisfaction] = useMutation(CONFIRM_THREAD_SATISFACTION, {
    onCompleted: () => {
      setState('success');
      setConfirming(false);
    },
    onError: (error) => {
      setErrorMessage(error.message || 'Failed to confirm');
      setConfirming(false);
    },
  });

  // Update state based on loading
  useEffect(() => {
    if (!slug) {
      setState('error');
      setErrorMessage('Invalid request URL');
    } else if (loading) {
      setState('loading');
    }
  }, [slug, loading]);

  // Handle sending chat message
  const handleSendMessage = useCallback(async (content: string) => {
    if (!slug || !content.trim() || posting) return;

    try {
      await postMessage({
        variables: {
          slug,
          input: { content: content.trim() },
        },
      });
    } catch {
      // Error handled in onError
    }
  }, [slug, posting, postMessage]);

  // Handle field change from form
  const handleFieldChange = useCallback(async (key: string, value: unknown) => {
    if (!slug) return;

    // Optimistic update
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setUpdatingField(true);

    try {
      await updateFields({
        variables: {
          slug,
          input: { fields: { [key]: value } },
        },
      });
    } catch {
      // Error handled in onError
    }
  }, [slug, updateFields]);

  // Handle confirm/submit
  const handleSubmit = useCallback(async () => {
    if (!slug || confirming) return;
    setConfirming(true);
    setErrorMessage('');

    try {
      await confirmSatisfaction({ variables: { slug } });
    } catch {
      // Error handled in onError
    }
  }, [slug, confirming, confirmSatisfaction]);

  const threadData = data?.requestThreadPublic;
  useDocumentTitle('Request', threadData?.parentStepTitle);

  // Build request spec for TaskInteractionPanel
  const requestSpec = threadData?.requestSpec ? {
    ask: threadData.requestSpec.ask || '',
    description: threadData.requestSpec.description || undefined,
    allowedFields: (threadData.requestSpec.allowedFields || []).map((f: { key: string; label: string; type: string; required: boolean; description?: string; enumValues?: string[] }) => ({
      key: f.key,
      label: f.label,
      type: f.type,
      required: f.required,
      description: f.description,
      enumValues: f.enumValues,
    })),
    uiMode: threadData.requestSpec.uiMode as 'form' | 'chat' | 'auto' | undefined,
  } : null;

  // Render loading state
  if (state === 'loading') {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '40px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>
            <Box sx={{ px: 2 }}>
              <Skeleton variant="text" height={40} />
              <Skeleton variant="rectangular" height={300} sx={{ my: 2 }} />
              <Skeleton variant="text" height={50} />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render closed state
  if (state === 'closed') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '40px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Request Closed
              </Typography>
              <Typography color="text.secondary">
                {errorMessage}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render error state
  if (state === 'error') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '40px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Something Went Wrong
              </Typography>
              <Typography color="text.secondary">
                {errorMessage}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render success state
  if (state === 'success') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '40px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Thank You!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your information has been submitted successfully.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render main interaction (ready state)
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <img
          src={logo}
          alt="Planapus Logo"
          style={{ height: '32px', width: 'auto' }}
        />
      </Box>

      <TaskInteractionPanel
        // Content
        title={threadData?.parentStepTitle || 'Information Request'}
        description={requestSpec?.ask}
        requestSpec={requestSpec}

        // State
        values={formValues}
        satisfaction={satisfaction}
        messages={messages}
        error={errorMessage || null}

        // Mode
        mode="delegate"

        // Callbacks
        onFieldChange={handleFieldChange}
        onSendMessage={handleSendMessage}
        onSubmit={handleSubmit}
        onClearError={() => setErrorMessage('')}

        // Loading states
        isFieldUpdating={updatingField}
        isChatSending={posting}
        isSubmitting={confirming}

        // Customization
        showHeader
        showProgress
        showChat
        chatCollapsible
      />
    </Container>
  );
}
